import React from 'react';
import { Link } from 'react-router-dom';
import { MapPin, Mail, Phone, Instagram, Facebook } from 'lucide-react';

const Footer = () => {
  return (
    <footer className="bg-[#3D2314] text-[#FDF6E3]">
      {/* Main Footer */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Brand */}
          <div className="space-y-6">
            <div className="flex items-center gap-3">
              <svg viewBox="0 0 60 60" className="w-14 h-14">
                <circle cx="30" cy="30" r="28" fill="url(#footerMonogramGradient)" />
                <circle cx="30" cy="30" r="25" fill="none" stroke="#FDF6E3" strokeWidth="0.5" opacity="0.4" />
                <path d="M18 18 L18 42 M18 30 L28 30 M28 18 L28 42" fill="none" stroke="#FDF6E3" strokeWidth="2.5" strokeLinecap="round" />
                <path d="M34 18 L34 42 M34 18 Q44 18 44 24 Q44 30 34 30 Q46 30 46 36 Q46 42 34 42" fill="none" stroke="#FDF6E3" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
                <path d="M50 14 Q48 18 50 22" fill="none" stroke="#D4A853" strokeWidth="1.5" strokeLinecap="round" />
                <circle cx="50" cy="12" r="2" fill="#D4A853" />
                <defs>
                  <linearGradient id="footerMonogramGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" stopColor="#C17817" />
                    <stop offset="100%" stopColor="#D4A853" />
                  </linearGradient>
                </defs>
              </svg>
              <div>
                <h3 className="font-serif text-2xl font-bold">House of Bania</h3>
                <p className="text-xs text-[#D4A853] tracking-wider">Heritage Taste</p>
              </div>
            </div>
            <p className="text-[#FDF6E3]/70 text-sm leading-relaxed">
              Bringing authentic Marwari flavors from India to your doorstep. 
              100% genuine products with heritage taste, crafted with love.
            </p>
            <div className="flex gap-4">
              <a href="#" className="w-10 h-10 rounded-full bg-[#C17817]/20 flex items-center justify-center hover:bg-[#C17817] transition-colors duration-300">
                <Instagram className="h-5 w-5" />
              </a>
              <a href="#" className="w-10 h-10 rounded-full bg-[#C17817]/20 flex items-center justify-center hover:bg-[#C17817] transition-colors duration-300">
                <Facebook className="h-5 w-5" />
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="font-serif text-lg font-semibold mb-6 text-[#D4A853]">Quick Links</h4>
            <ul className="space-y-3">
              {['Home', 'Products', 'Our Story', 'Contact'].map((item) => (
                <li key={item}>
                  <Link
                    to={item === 'Home' ? '/' : `/${item.toLowerCase().replace(' ', '-')}`}
                    className="text-[#FDF6E3]/70 hover:text-[#D4A853] transition-colors duration-300 text-sm"
                  >
                    {item}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Product Categories */}
          <div>
            <h4 className="font-serif text-lg font-semibold mb-6 text-[#D4A853]">Our Products</h4>
            <ul className="space-y-3">
              {['Cold Pressed Mustard Oil', 'Premium Makhana', 'Traditional Pickles', 'Marwari Snacks'].map((item) => (
                <li key={item}>
                  <Link
                    to="/products"
                    className="text-[#FDF6E3]/70 hover:text-[#D4A853] transition-colors duration-300 text-sm"
                  >
                    {item}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Shipping Regions */}
          <div>
            <h4 className="font-serif text-lg font-semibold mb-6 text-[#D4A853]">We Ship To</h4>
            <ul className="space-y-3">
              {[
                { city: 'Singapore', flag: '🇸🇬' },
                { city: 'Dubai, UAE', flag: '🇦🇪' },
                { city: 'Australia', flag: '🇦🇺' },
                { city: 'United Kingdom', flag: '🇬🇧' },
                { city: 'India', flag: '🇮🇳' },
              ].map((item) => (
                <li key={item.city} className="flex items-center gap-2 text-[#FDF6E3]/70 text-sm">
                  <span>{item.flag}</span>
                  <span>{item.city}</span>
                </li>
              ))}
            </ul>
            <div className="mt-6 space-y-2">
              <a href="mailto:hello@houseofbania.com" className="flex items-center gap-2 text-[#FDF6E3]/70 hover:text-[#D4A853] transition-colors text-sm">
                <Mail className="h-4 w-4" />
                hello@houseofbania.com
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-[#FDF6E3]/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-[#FDF6E3]/50 text-sm">
              © 2025 House of Bania. All rights reserved.
            </p>
            <div className="flex gap-6">
              <Link to="/privacy" className="text-[#FDF6E3]/50 hover:text-[#D4A853] text-sm transition-colors">
                Privacy Policy
              </Link>
              <Link to="/terms" className="text-[#FDF6E3]/50 hover:text-[#D4A853] text-sm transition-colors">
                Terms of Service
              </Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
